import React, { FC, useState } from 'react';
import { Button, Col, Menu, Modal, Row } from 'antd';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { PushpinOutlined, PushpinFilled } from '@ant-design/icons';
import { ReactSVG } from 'react-svg';
import useLocalStorageService from "../../../services/localStorage";
import MenuItems from "./MenuItems";


const MenuItem: FC<{
    id,
    index: any, moveItem, children
}> = ({ id, index, moveItem, children }) => {
    const ref = React.useRef(null);

    const [, drop] = useDrop({
        accept: 'MenuItem',
        hover(item: any, monitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            if (dragIndex === hoverIndex) {
                return;
            }

            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            moveItem(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag] = useDrag({
        type: 'MenuItem',
        item: { type: 'MenuItem', id, index },
        collect: monitor => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const opacity = isDragging ? 0.5 : 1;

    drag(drop(ref));

    return (
        <div ref={ref} style={{ opacity }}>
            {children}
        </div>
    );
};

const DraggableMenu: FC<{
    items: MenuItems[],
    onTogglePin(item: MenuItems): void,
    onClose?: () => void,
    onDrag?: (items: MenuItems[]) => void
}> = ({ items, onTogglePin, onClose, onDrag }) => {
    const [isModalVisible, setIsModalVisible] = useState<boolean>(true);
    const [menuItems, setMenuItems] = useState(items);
    const { setLocalStorageItem } = useLocalStorageService();

    const moveItem = (dragIndex, hoverIndex) => {
        const draggedItem = menuItems[dragIndex];
        const updatedItems = [...menuItems];
        updatedItems.splice(dragIndex, 1);
        updatedItems.splice(hoverIndex, 0, draggedItem);
        setMenuItems(updatedItems);
        setLocalStorageItem("menuItems", updatedItems);
        onDrag && onDrag(updatedItems);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        onClose && onClose();
    };
    const handleOk = () => {
        setIsModalVisible(false);
    };

    const handlePinToggle = (_item) => {
        _item.pinned = !_item.pinned;
        setMenuItems(menuItems.map(item => {
            if (item.id === _item.id) {
                return { ...item, pinned: _item.pinned };
            }
            return item;
        }));
        onTogglePin(_item);
        setLocalStorageItem("menuItems", menuItems);
    }

    return (
        <Modal
            title="Menu"
            visible={isModalVisible}
            onOk={handleOk}
            onCancel={handleCancel}
            width="350px"
            className="beam-modal"
            footer={[]}
            destroyOnClose={true}
            maskClosable={false}
            keyboard={false}
        >
            <DndProvider backend={HTML5Backend}>
                <div className='scroll-bar' style={{ height: "400px" }}>
                    {menuItems.map((item, index) => (
                        <MenuItem key={'key' + item.id} id={item.id} index={index} moveItem={moveItem}>
                            <Row style={{ clear: "both" }} className="person-item">
                                <Col span={4}>
                                    <ReactSVG
                                        className={item.cssClass}
                                        src={item.src}
                                    />
                                </Col>
                                <Col span={14}>
                                    <span>
                                        <strong>{item.name}</strong>
                                    </span>
                                </Col>
                                <Col span={6}>
                                    <Button className='beam-btn no-shadow pull-right' onClick={() => handlePinToggle(item)}>
                                        {item.pinned ? <PushpinFilled className='text-primary' /> : <PushpinOutlined />}
                                    </Button>
                                </Col>
                            </Row>
                        </MenuItem>
                    ))}
                </div>
            </DndProvider>
        </Modal>
    );
};

export default DraggableMenu;
